import { type UseQueryResult, useQuery } from "@tanstack/react-query";

import type { AllowlistedTable } from "@busright/data-contract";

// The /api/explore/tables endpoint surfaces the server-enforced allowlist so the picker renders
// from the same source of truth as the explore/export endpoints. It is intentionally NOT enveloped
// (no meta) — it's a static config list, not a data surface — so it returns `{ tables: [...] }`.
interface ExploreTablesResponse {
  readonly tables: readonly AllowlistedTable[];
}

const getExploreTables = async (): Promise<ExploreTablesResponse> => {
  const res = await fetch("/api/explore/tables", { credentials: "include" });
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) as { error?: string } | null;
    throw new Error(body?.error ?? `HTTP ${res.status}`);
  }
  return (await res.json()) as ExploreTablesResponse;
};

// Lives in its own file (not the shared queries.ts) to avoid colliding with parallel work on the
// shared hooks module. The allowlist is static for the session, so it never goes stale.
export const useExploreTables = (): UseQueryResult<ExploreTablesResponse> =>
  useQuery({
    queryKey: ["explore", "tables"] as const,
    queryFn: getExploreTables,
    staleTime: Number.POSITIVE_INFINITY,
  });
